/**
 * State management utilities for preventing race conditions and ensuring data consistency
 */

import { useEffect, useMemo, useRef } from 'react';
import debounce from 'lodash.debounce';
import throttle from 'lodash.throttle';

/**
 * Debounce hook using lodash.debounce
 * @param callback - Function to debounce
 * @param delay - Delay in milliseconds
 * @returns Debounced function
 */
export function useDebounce<T extends (...args: Parameters<T>) => void>(
  callback: T,
  delay: number
): (...args: Parameters<T>) => void {
  const callbackRef = useRef(callback);

  // Update callback ref on each render
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Create debounced function with lodash
  const debouncedFn = useMemo(
    () => debounce((...args: Parameters<T>) => {
      callbackRef.current(...args);
    }, delay),
    [delay]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedFn.cancel();
    };
  }, [debouncedFn]);

  return debouncedFn;
}

/**
 * Throttle hook using lodash.throttle
 * @param callback - Function to throttle
 * @param delay - Delay in milliseconds
 * @returns Throttled function
 */
export function useThrottle<T extends (...args: Parameters<T>) => void>(
  callback: T,
  delay: number
): (...args: Parameters<T>) => void {
  const callbackRef = useRef(callback);

  // Update callback ref on each render
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Create throttled function with lodash
  const throttledFn = useMemo(
    () => throttle((...args: Parameters<T>) => {
      callbackRef.current(...args);
    }, delay),
    [delay]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      throttledFn.cancel();
    };
  }, [throttledFn]);

  return throttledFn;
}

// Re-export domain-specific utilities from state-locks
export {
  OperationLock,
  useOperationLock,
  OptimisticUpdateManager,
  useOptimisticUpdate,
  StateVersionManager,
  useStateVersion,
  type OptimisticUpdate,
} from './state-locks';

// Re-export lodash utilities for convenience
export { default as debounce } from 'lodash.debounce';
export { default as throttle } from 'lodash.throttle';